import React, { useEffect, useMemo } from 'react'
import { generatePath, Navigate, Outlet } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { isWithinInterval } from 'date-fns'

import Header from '@components/Header'
import LinkButton from '@components/LinkButton'
import AccessErrorPage from '@components/AccessError'

import routes from '@src/Routes/routes'
import { useGetCostCenterDetail } from '@queries/Accounting/costCenter'
import useGetUUIDFromPath from '@helpers/hooks/useGetUUIDFromPath'
import { useHasPermission } from '@helpers/hooks/useHasPermission'
import { Perm } from '@type/permissions'
import useStore from '@pages/Accounting/CostCenter/store'

import { BudgetFrequency } from '@type/budget'

export interface DetailsProps {}

export const Details: React.FC<DetailsProps> = () => {
  const { t } = useTranslation('common')
  const { id } = useGetUUIDFromPath()

  const { data: costCenterDetail, error } = useGetCostCenterDetail(id)
  // The edit route is ACCOUNTING_MANAGE-gated; without this a Member hits
  // ProtectedRoute's redirect and lands on their fallback route (/reports).
  const canManage = useHasPermission(Perm.ACCOUNTING_MANAGE)
  const setFilter = useStore.use.setFilter()
  const setPeriodOptions = useStore.use.setPeriodOptions()

  const periodOptions = useMemo(() => {
    let availableBudgets = costCenterDetail?.budget_allocations

    if (costCenterDetail?.budget_frequency !== BudgetFrequency.custom) {
      availableBudgets = availableBudgets?.filter(
        (budget, index) =>
          costCenterDetail?.budget_allocations?.findIndex(
            (searchBudget) => searchBudget.start_date === budget.start_date,
          ) === index,
      )
    }

    return (
      availableBudgets?.map(({ name, start_date: value }) => {
        const label = costCenterDetail?.budget_frequency === BudgetFrequency.yearly ? `${name} - ${+name + 1}` : name
        return { label, value }
      }) || []
    )
  }, [costCenterDetail?.budget_frequency])

  useEffect(() => {
    if (periodOptions) {
      setPeriodOptions(periodOptions)
    }
  }, [periodOptions])

  useEffect(() => {
    const period = costCenterDetail?.budget_allocations?.find(({ start_date, end_date }) => {
      return isWithinInterval(new Date(), {
        start: new Date(start_date),
        end: new Date(end_date),
      })
    })
    setFilter('period', period?.start_date || costCenterDetail?.budget_allocations?.[0]?.start_date)
  }, [])

  // A cost center outside the user's team/org returns 403/404; handle it
  // gracefully instead of letting the query throw to the app error boundary.
  if (error?.response?.status === 403) {
    return <AccessErrorPage />
  }
  if (error?.response?.status === 404) {
    return <Navigate to={routes.public.notFound} replace />
  }

  return (
    <>
      <Header
        backUrl={routes.private.costCenter}
        title={costCenterDetail?.name}
        buttons={
          canManage ? (
            <LinkButton
              to={generatePath(routes.private.costCenterEditById, { id })}
              type="outlined"
              state={{
                backRoute: location.pathname,
              }}
            >
              {t('edit')}
            </LinkButton>
          ) : null
        }
      />
      <div className="base flex flex-col gap-3 p-4">
        <Outlet />
      </div>
    </>
  )
}

export default Details
